import React, { useState } from 'react';
import styled from 'styled-components';
import { Switch, Text, Tooltip } from '@amzn/storm-ui-v3';
import { ICellRendererParams } from '@ag-grid-community/core';
import { SUBSCRIPTION_FREQUENCY, SUBSCRIPTION_STATUS } from '@urc-ui/utils/types';
import { SUBSCRIPTION_FREQUENCY_TRANSLATION_MAP, SUBSCRIPTION_STATUS_MAP } from '@urc-ui/utils/subscription-utils';
import { UpdateSubscriptionStatusRequest } from '@urc-ui/store/api/subscriptions/types';
import { useUpdateSubscriptionStatusMutation } from '@urc-ui/store/api/subscriptions/subscriptions';
import { useTranslateMapItem } from '@urc-ui/hooks/translate-map';
import { QueryIcons } from '@urc-ui/components/icons/query-icons';
import { StyledCellWrapper } from '@urc-ui/components/styles';
import { TAKT_EVENT, TAKT_EVENT_SOURCE, useTaktLogger } from '@urc-ui/hooks/takt-logger';
import { isSpoofer, isUiRossCutoverEnabled } from '@urc-ui/utils/app-utils';
import { useTranslation } from 'react-i18next';
import { useEditSubscriptionMutation } from '@urc-ui/store/api/ross/subscriptions/subscriptions';
import { getTimezone } from '@urc-ui/services/app-context';
import { buildEditSubscriptionRequest } from '@urc-ui/store/api/ross/transformers/sspa-to-ross-transformers';
import { logger } from '@urc-ui/services/logger';

const StyledContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const StyledStatusDiv = styled.div`
  display: inline-block;
`;

const StyledScheduleTypeText = styled(Text)`
  color: ${({ theme }) => theme.palette.boulder};
`;

const StyledSwitch = styled(Switch)`
  margin-left: ${({ theme }) => theme.spacing.base};
`;

interface SubscriptionStatusSwitchProps {
  readonly toggle: boolean;
  readonly subscriptionId: string;
  readonly status: string;
  readonly frequency: string;
  readonly onSwitchToggled: (toggle: boolean) => void;
}

const SubscriptionStatusSwitch = (props: SubscriptionStatusSwitchProps) => {
  const { t: translate } = useTranslation();
  const { toggle, status, subscriptionId } = props;
  // eslint-disable-next-line
  const [, { isError: isUrcError, error: urcError, isSuccess: isUrcSuccess, isLoading: isUrcLoading }] = useUpdateSubscriptionStatusMutation({
      fixedCacheKey: subscriptionId
    });
  const [, { data: rossResponse, isError: isRossError, error: rossError, isSuccess: isRossSuccess, isLoading: isRossLoading }] =
    useEditSubscriptionMutation({
      fixedCacheKey: subscriptionId
    });
  const isError = isUiRossCutoverEnabled() ? isRossError || (isRossSuccess && !rossResponse?.success?.length) : isUrcError;
  const isLoading = isUiRossCutoverEnabled() ? isRossLoading : isUrcLoading;
  const isSuccess = isUiRossCutoverEnabled() ? isRossSuccess && !!rossResponse?.success?.length : isUrcSuccess;
  const error = isUiRossCutoverEnabled() ? rossError : urcError;

  // don't show switch for subscriptions that don't have either paused or active status
  if (status !== SUBSCRIPTION_STATUS.PAUSED && status !== SUBSCRIPTION_STATUS.ACTIVE) {
    return null;
  }

  const errorMessage = (error as any)?.errorMessage as string;

  const toggleProps = {
    id: 'subscription-status-switch',
    small: true,
    toggled: toggle,
    onChange: props.onSwitchToggled
  };
  const tooltip = translate(
    'sspa_rcm_ad_reports_unauthorized_spoofer_update_message',
    `Internal users can’t edit reports created by advertisers while spoofing.`
  );

  return (
    <div>
      <QueryIcons isError={isError} isLoading={isLoading} isSuccess={isSuccess} errorMessage={errorMessage} />
      {isSpoofer() ? (
        <Tooltip
          trigger={
            <span>
              <StyledSwitch disabled={true} {...toggleProps} />
            </span>
          }
          message={tooltip}
          type={'dark'}
        />
      ) : (
        <StyledSwitch {...toggleProps} />
      )}
    </div>
  );
};

const computeStatus = (toggle: boolean) => (toggle ? SUBSCRIPTION_STATUS.ACTIVE : SUBSCRIPTION_STATUS.PAUSED);

const StatusCellRenderer = (props: ICellRendererParams) => {
  const subscription = props.value.subscription;
  const [subscriptionStatus, setSubscriptionStatus] = useState(subscription.status);
  const subscriptionFrequency = subscription.subscriptionDelivery.frequency as SUBSCRIPTION_FREQUENCY;
  const [toggle, setToggle] = useState(subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE);
  const [updateSubscriptionStatus] = useUpdateSubscriptionStatusMutation({ fixedCacheKey: subscription.id });
  const [editSubscriptionStatus] = useEditSubscriptionMutation({ fixedCacheKey: subscription.id });

  const subscriptionStatusString = useTranslateMapItem(SUBSCRIPTION_STATUS_MAP, subscriptionStatus);
  const subscriptionFrequencyString = useTranslateMapItem(SUBSCRIPTION_FREQUENCY_TRANSLATION_MAP, subscriptionFrequency);
  const { logCustomTaktEvent } = useTaktLogger({ subscription: subscription });

  const onSwitchToggled = async (newToggleState: boolean) => {
    try {
      setToggle(newToggleState);
      setSubscriptionStatus(computeStatus(newToggleState));
      const status = computeStatus(newToggleState);

      if (isUiRossCutoverEnabled()) {
        const request = buildEditSubscriptionRequest({
          id: subscription.id,
          status: status,
          timezone: getTimezone()
        });
        const response = await editSubscriptionStatus(request)
          .unwrap()
          .catch(error => {
            throw error;
          });
        if (response?.failed && response.failed.length > 0) {
          logger.logFatalMessage('there was an error while updating subscription status', response, subscription);
          throw new Error(`there was an error while updating subscription status ${response} - ${subscription}`);
        }
      } else {
        const payload: UpdateSubscriptionStatusRequest = {
          id: subscription.id,
          status
        };

        await updateSubscriptionStatus(payload)
          .unwrap()
          .catch(error => {
            throw error;
          });
      }

      const rowNode = props.api.getRowNode(subscription.id);
      rowNode?.updateData({
        ...props.value.subscription,
        status
      });
      logCustomTaktEvent({ eventName: TAKT_EVENT.TOGGE_RECURRING_REPORT, source: TAKT_EVENT_SOURCE.SUBSCRIPTION_TABLE });
    } catch (error) {
      setToggle(!newToggleState);
      setSubscriptionStatus(computeStatus(!newToggleState));
    }
  };

  return (
    <StyledCellWrapper>
      <StyledContainer>
        <StyledStatusDiv>
          <Text fontSize='small'>{subscriptionStatusString || subscriptionStatus}</Text>
          <StyledScheduleTypeText fontSize='small'>{subscriptionFrequencyString || subscriptionFrequency}</StyledScheduleTypeText>
        </StyledStatusDiv>
        <SubscriptionStatusSwitch
          toggle={toggle}
          status={subscriptionStatus}
          frequency={subscriptionFrequency}
          onSwitchToggled={onSwitchToggled}
          subscriptionId={subscription.id}
        />
      </StyledContainer>
    </StyledCellWrapper>
  );
};

export { StatusCellRenderer };
